declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    umami?: {
      track(name: string, data?: AnalyticsData): void;
    };
  }
}

export type AnalyticsData = Record<string, string | number | boolean>;

const VISITOR_ID_KEY = "pp_visitor_id";
const UTM_STORAGE_KEY = "pp_utm_params";
const LANDING_ATTRIBUTION_KEY = "pp_landing_attribution";

export interface LandingAttribution {
  landingPage?: string;
  variantKey?: string;
  experimentId?: number;
}

export function trackPageView(path: string, title?: string) {
  if (typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
  });
}

export function trackEvent(name: string, params?: AnalyticsData): void {
  if (typeof window === "undefined") return;

  try {
    window.umami?.track(name, params);
  } catch {
    // Replit Analytics must never break the app.
  }

  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch {
    // GA4 must never break the app either.
  }
}

/**
 * Returns a durable, anonymous per-browser id used only to count unique
 * visitors to first-party landing pages. Persisted in localStorage so it
 * survives across sessions; falls back gracefully if storage is unavailable
 * (e.g. private browsing) by generating a fresh id for that page load.
 */
export function getVisitorId(): string {
  try {
    let id = window.localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id = typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export interface UtmParams {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/**
 * Reads utm_source/utm_medium/utm_campaign from the current URL, if present,
 * and persists them to sessionStorage so they remain available for the rest
 * of the visit (e.g. when a lead-capture form on the same page is submitted
 * after the initial page load). If no UTM params are present on this load,
 * falls back to whatever was captured earlier in the session.
 */
export function getUtmParams(): UtmParams {
  try {
    const search = new URLSearchParams(window.location.search);
    const fromUrl: UtmParams = {
      utmSource: search.get("utm_source") || undefined,
      utmMedium: search.get("utm_medium") || undefined,
      utmCampaign: search.get("utm_campaign") || undefined,
    };
    const hasUrlParams = fromUrl.utmSource || fromUrl.utmMedium || fromUrl.utmCampaign;
    if (hasUrlParams) {
      window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
    const stored = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as UtmParams;
    return {};
  } catch {
    return {};
  }
}

export function getLandingAttribution(): LandingAttribution {
  try {
    const stored = window.sessionStorage.getItem(LANDING_ATTRIBUTION_KEY);
    return stored ? JSON.parse(stored) as LandingAttribution : {};
  } catch {
    return {};
  }
}

export function setLandingAttribution(attribution: LandingAttribution): void {
  try {
    window.sessionStorage.setItem(LANDING_ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // Attribution is helpful but must never block a visitor.
  }
}

export function trackLandingEvent(
  eventType: string,
  pageUrl: string,
  metadata?: Record<string, unknown>,
): void {
  const attribution = getLandingAttribution();
  trackServerEvent(eventType, pageUrl, {
    ...metadata,
    landingPage: attribution.landingPage || pageUrl,
    variantKey: attribution.variantKey || "control",
    experimentId: attribution.experimentId,
  });
}

/**
 * Records a first-party visit to a landing page in our own database (distinct
 * from the GA4 pageview beacon), tagging it with a visitor id and any UTM
 * attribution so an internal dashboard can report on it without needing a
 * separate Google Analytics API integration.
 */
export function trackServerPageView(pageUrl: string, eventType: string) {
  trackServerEvent(eventType, pageUrl);
}

/**
 * Records an attributed first-party event in our own database.
 *
 * GA4 events remain separate so the admin portal does not depend on a
 * third-party analytics API for campaign and funnel reporting.
 */
export function trackServerEvent(
  eventType: string,
  pageUrl: string = window.location.pathname,
  metadata?: Record<string, unknown>,
) {
  try {
    const utm = getUtmParams();
    fetch("/api/analytics/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType,
        pageUrl,
        visitorId: getVisitorId(),
        utmSource: utm.utmSource,
        utmMedium: utm.utmMedium,
        utmCampaign: utm.utmCampaign,
        metadata,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Tracking must never break the page.
  }
}

/**
 * Records a button/CTA click on a marketing landing page.
 * @param label  Human-readable label of the button (e.g. "Open a Free Account").
 * @param location  Coarse location on the page (e.g. "hero", "bottom-cta", "pricing-card").
 */
export function trackButtonClick(label: string, location: string) {
  trackServerEvent("button_click", window.location.pathname, {
    buttonLabel: label,
    buttonLocation: location,
  });
}

/**
 * Sets up time-on-page tracking for a marketing landing page. Call in a
 * useEffect and return the cleanup function.
 *
 * Fires a `page_time_spent` event when the visitor leaves the page (either
 * tab-close / navigation-away via beforeunload/visibilitychange, or SPA
 * navigation when the component unmounts). The `sent` flag ensures only one
 * event fires per page mount regardless of which exit path triggers first.
 *
 * @param pageUrl  Canonical path to record (e.g. "/dscsa").
 * @returns cleanup function — pass directly as the useEffect return value.
 */
export function setupPageTimeTracking(pageUrl: string): () => void {
  const startTime = Date.now();
  let sent = false;

  function send() {
    if (sent) return;
    sent = true;
    const durationSeconds = Math.round((Date.now() - startTime) / 1000);
    if (durationSeconds < 2) return; // ignore flash-of-content renders
    const payload = JSON.stringify({
      eventType: "page_time_spent",
      pageUrl,
      visitorId: getVisitorId(),
      ...(() => {
        const utm = getUtmParams();
        return {
          utmSource: utm.utmSource,
          utmMedium: utm.utmMedium,
          utmCampaign: utm.utmCampaign,
        };
      })(),
      metadata: { durationSeconds },
    });
    try {
      // sendBeacon is best-effort for beforeunload/visibilitychange;
      // fall back to fetch with keepalive for broader compatibility.
      if (typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon(
          "/api/analytics/event",
          new Blob([payload], { type: "application/json" }),
        );
      } else {
        fetch("/api/analytics/event", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      // Never break the page.
    }
  }

  const handleBeforeUnload = () => send();
  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") send();
  };

  window.addEventListener("beforeunload", handleBeforeUnload);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    // Fire on SPA navigation (component unmount); beforeunload won't fire for
    // in-app route changes, so this is the only exit path in that scenario.
    send();
  };
}
